// src/features/records/hooks/useUserProfiles.js
import { useState, useEffect, useMemo } from 'react';
import { getUserProfile } from '../../../services/userService';
import { createProfileCache } from '../utils/profileCache';

/**
 * Hook to fetch and cache user profiles for record authors
 * @param {Array} userIds - Array of user IDs to fetch profiles for
 * @returns {Object} Map of userId to user profile data
 */
// Create a singleton cache instance
const profileCache = createProfileCache({ maxSize: 100, ttl: 5 * 60 * 1000 });

// Export cache management functions
export const clearProfileCache = () => {
  profileCache.clear();
  console.log('[ProfileCache] Cache cleared');
};

export const getProfileCacheSize = () => profileCache.size();

const useUserProfiles = (userIds = []) => {
  const [profiles, setProfiles] = useState({});
  const [loading, setLoading] = useState(false);

  // Create a stable string key for dependency tracking
  const userIdsKey = useMemo(() => {
    if (!userIds || userIds.length === 0) return '';
    return [...new Set(userIds)].filter(Boolean).sort().join(',');
  }, [userIds]);

  useEffect(() => {
    // Early return for empty user IDs
    if (!userIdsKey) return;

    const abortController = new AbortController();
    let isMounted = true;

    const fetchProfiles = async () => {
      setLoading(true);
      const newProfiles = {};
      
      // Parse unique user IDs from the key
      const currentUserIds = userIdsKey.split(',').filter(Boolean);
      
      // Clean expired entries and enforce size limit
      profileCache.evictExpired();
      profileCache.evictOldest();
      
      // Filter out already cached profiles
      const idsToFetch = currentUserIds.filter(id => id && !profileCache.has(id));
      
      if (idsToFetch.length === 0) {
        // All profiles are already cached
        const cachedProfiles = {};
        currentUserIds.forEach(id => {
          if (id) {
            const profile = profileCache.get(id);
            if (profile) {
              cachedProfiles[id] = profile;
            }
          }
        });
        if (isMounted && !abortController.signal.aborted) {
          setProfiles(cachedProfiles);
          setLoading(false);
        }
        return;
      }

      // Fetch profiles in parallel
      const fetchPromises = idsToFetch.map(async (userId) => {
        // Check if aborted before making request
        if (abortController.signal.aborted) {
          return;
        }

        try {
          const profile = await getUserProfile(userId);
          
          // Check if aborted after receiving response
          if (abortController.signal.aborted) {
            return;
          }
          
          // Cache the profile
          profileCache.set(userId, profile);
          newProfiles[userId] = profile;
        } catch (error) {
          // Don't log errors if the request was aborted
          if (!abortController.signal.aborted) {
            console.error(`Error fetching profile for user ${userId}:`, error);
            // Cache a default profile to avoid repeated failed fetches
            const defaultProfile = {
              id: userId,
              displayName: null,
              username: null,
              profilePictureUrl: null
            };
            profileCache.set(userId, defaultProfile);
            newProfiles[userId] = defaultProfile;
          }
        }
      });

      await Promise.all(fetchPromises);

      // Check if aborted before updating state
      if (abortController.signal.aborted) {
        return;
      }

      // Combine new profiles with cached ones
      const allProfiles = {};
      currentUserIds.forEach(id => {
        if (id) {
          const profile = profileCache.get(id);
          if (profile) {
            allProfiles[id] = profile;
          }
        }
      });

      if (isMounted && !abortController.signal.aborted) {
        setProfiles(allProfiles);
        setLoading(false);
      }
    };

    fetchProfiles();

    return () => {
      isMounted = false;
      abortController.abort();
    };
  }, [userIdsKey]); // Dependency on the user IDs key string

  return { profiles, loading };
};

/**
 * Hook to fetch a single user profile
 * @param {string} userId - User ID to fetch profile for
 * @returns {Object} User profile data
 */
export const useUserProfile = (userId) => {
  const { profiles, loading } = useUserProfiles(userId ? [userId] : []);
  return {
    profile: profiles[userId] || null,
    loading
  };
};

export default useUserProfiles;
